import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  CssBaseline,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  LinearProgress,
} from "@mui/material";
import {
  ContentBox,
  StyledButton,
  TitleText,
  PageContainer,
} from "./Default.js";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import CreditsBanner from "../Components/CreditsNudge";
import XmlToJsonConverterContent from "../Contents/XmlToJsonContents";
import { message } from "../Constants/message.js";
import { xml2json } from "xml-js";
import CommentSection from "../Components/CommentSection.js";

export default function XmlToJsonConverter({ user, login, isLoggedIn }) {
  const [xmlInput, setXmlInput] = useState("");
  const [jsonOutput, setJsonOutput] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const savedXml = localStorage.getItem("xmlInput");
    if (savedXml) {
      setXmlInput(savedXml);
    }
  }, []);

  useEffect(() => {
    if (xmlInput) {
      localStorage.setItem("xmlInput", xmlInput);
    }
  }, [xmlInput]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (
      file &&
      (file.type === "text/xml" ||
        file.type === "application/xml" ||
        file.name.endsWith(".xml"))
    ) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setXmlInput(e.target.result);
      };
      reader.readAsText(file);
    } else {
      setError("Please upload a valid XML file.");
    }
  };

  const handleConvert = async () => {
    setError("");
    setSuccess("");
    setLoading(true);
  
    try {
      // Detect tool key from current path
      const currentPath = window.location.pathname.replace(/^\/+|\/+$/g, "");
      const toolKey = currentPath.replace(/[\/]/g, "_") || "unknown_tool";
      const guestUsedKey = `${toolKey}_guestUsed`;
  
      const isGuestUsed = localStorage.getItem(guestUsedKey);
  
      if (!isLoggedIn && isGuestUsed) {
        setError(message.loginPrompt);
        setTimeout(() => {
          login();
        }, 1500);
        return;
      }
  
      if (isLoggedIn && user.account_type.toLowerCase() === "free") {
        if (credits.remaining < 1) {
          setError("Insufficient credits to convert XML. Please upgrade.");
          return;
        }
      }
  
      if (!xmlInput.trim()) {
        throw new Error("Please enter XML data to convert");
      }
  
      const result = xml2json(xmlInput, {
        compact: true,
        spaces: 2,
        ignoreComment: true,
        ignoreDeclaration: true,
        ignoreInstruction: true,
        ignoreAttributes: false,
        textKey: "_text",
        cdataKey: "_cdata",
        alwaysChildren: false,
        nativeType: true,
        trim: true,
      });
  
      const jsonObj = JSON.parse(result);
  
      const cleanObject = (obj) => {
        if (typeof obj !== "object" || obj === null) return obj;
        if (Array.isArray(obj)) return obj.map(cleanObject);
  
        const newObj = {};
        for (const key in obj) {
          const value = obj[key];
  
          if (key === "_attributes") {
            for (const attrKey in value) {
              newObj[`_${attrKey}`] = value[attrKey];
            }
          } else if (typeof value === "object" && value !== null) {
            if ("_text" in value) {
              newObj[key] = value._text;
            } else {
              newObj[key] = cleanObject(value);
            }
          } else {
            newObj[key] = value;
          }
        }
  
        return newObj;
      };
  
      const cleanedJson = cleanObject(jsonObj);
  
      // Update credits if logged in
      if (isLoggedIn) {
        if (user.account_type.toLowerCase() === "free") {
          const creditsResponse = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/credit/updateCredits`,
            { toolName: "xml_to_json_converter" }
          );
          setCredits({
            remaining: creditsResponse.data.remaining_credits || 0,
            availed: creditsResponse.data.availed_credits || 0,
            total: creditsResponse.data.total_credits || 0,
          });
        } else if (user.account_type.toLowerCase() === "paid") {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/credit/updateUsage`,
            { toolName: "xml_to_json_converter" }
          );
        }
      } else {
        // If not logged in, mark one-time access
        localStorage.setItem(guestUsedKey, "true");
      }
  
      setJsonOutput(JSON.stringify(cleanedJson, null, 2));
      setSuccess("Successfully converted to JSON! Download or copy below.");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  

  const handleDownloadClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const copyToClipboard = () => {
    navigator.clipboard.writeText(jsonOutput);
    setSuccess("JSON copied to clipboard!");
  };
  const handleDownloadClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = () => {
    const blob = new Blob([jsonOutput], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "converted.json";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setAnchorEl(null);
    setSuccess("JSON downloaded!");
  };

  return (
    <>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />

      <PageContainer sx={{ paddingTop: 3 }}>
        <ContentBox>
          <TitleText variant="h4" align="center">
            XML to JSON Converter 🔄
          </TitleText>
          <Box mt={1} display="flex" flexDirection="column" gap={2}>
            <StyledButton variant="contained" component="label">
              Upload XML File
              <input
                type="file"
                hidden
                accept=".xml,text/xml,application/xml"
                onChange={handleFileUpload}
              />
            </StyledButton>
            <Typography variant="body1" color="textPrimary" align="center">
              OR
            </Typography>
            <TextField
              label="Paste XML Here"
              multiline
              minRows={6}
              value={xmlInput}
              onChange={(e) => setXmlInput(e.target.value)}
              placeholder='<?xml version="1.0"?><note><to>Alice</to><from>Bob</from></note>'
              sx={{
                maxHeight: 400,
                overflow: "auto",
                mt: 2,
                "& .MuiInputLabel-root": {
                  color: "text.primary",
                  position: "absolute",
                  left: 0,
                  top: -10,
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  "&.Mui-focused": {
                    color: "primary.main",
                  },
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "grey.300",
                  },
                  "&:hover fieldset": {
                    borderColor: "primary.light",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                },
              }}
              InputProps={{
                style: {
                  fontFamily: "monospace",
                  fontSize: "0.875rem",
                  paddingTop: "8px",
                },
              }}
              variant="outlined"
              fullWidth
            />
            {isLoggedIn && user.account_type.toLowerCase() === "free" ? (
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                mt={2}
              >
                <b>⚠️ Free Plan Limitations:</b> For Free plan users, 1 credit
                will be deducted for each conversion. <b>All Paid plan users</b>{" "}
                enjoy this tool for free 🎁 <b>without any credit deduction</b>.
              </Typography>
            ) : (
              isLoggedIn && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  mt={2}
                >
                  <b>
                    🎁 Good news! For all Paid plan users, conversions and
                    downloads are completely complimentary — no credits will be
                    deducted.
                  </b>
                </Typography>
              )
            )}

            {loading && <LinearProgress />}

            <StyledButton
              variant="contained"
              color="success"
              onClick={handleConvert}
              disabled={loading}
            >
              {loading ? "Converting..." : "Convert to JSON"}
            </StyledButton>

            {success && (
              <Alert severity="success" onClose={() => setSuccess("")}>
                {success}
              </Alert>
            )}
            {error && (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            {jsonOutput && (
              <>
                <Box display="flex" gap={1} justifyContent="center" mb={2}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={copyToClipboard}
                    aria-label="Copy JSON"
                  >
                    <ContentCopyIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleDownloadClick}
                    aria-label="Download JSON"
                  >
                    <DownloadIcon />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleDownloadClose}
                  >
                    <MenuItem onClick={handleDownload}>Download JSON</MenuItem>
                  </Menu>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    minHeight: 150,
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                  }}
                >
                  {jsonOutput}
                </Box>
              </>
            )}
          </Box>
        </ContentBox>
        <CommentSection
          toolId="xml-to-json-converter"
          user={user}
          login={login}
          isLoggedIn={isLoggedIn}
        />
        <XmlToJsonConverterContent />
      </PageContainer>
    </>
  );
}
